import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import * as adminApi from '../api/adminApi';
import { PAGE_SIZE, formatDate } from '../lib/format';
import { normalizeListResponse, pickErrorMessage } from '../lib/apiHelpers';
import { PageHeader, Card, CardBody, ErrorBanner } from '../components/PageChrome';
import { DataTable } from '../components/DataTable';
import { Pagination } from '../components/Pagination';
import { ConfirmDialog } from '../components/Modal';
import { IconTrash } from '../components/Icons';
import { FilterSubmitButton } from '../components/FilterSubmitButton';

export default function Reviews() {
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [skip, setSkip] = useState(0);
  const [limit] = useState(PAGE_SIZE);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [rating, setRating] = useState('');
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = { skip, limit };
      if (rating) params.rating = Number(rating);
      const raw = await adminApi.listReviews(params);
      const norm = normalizeListResponse(raw, limit);
      setRows(norm.items);
      setTotalCount(norm.totalCount);
    } catch (e) {
      setError(pickErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [skip, limit, rating]);

  useEffect(() => {
    load();
  }, [load]);

  async function confirmDelete() {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await adminApi.deleteReview(deleteId);
      setDeleteId(null);
      await load();
    } catch (err) {
      setError(pickErrorMessage(err));
    } finally {
      setDeleting(false);
    }
  }

  const columns = [
    {
      key: 'rating',
      label: 'Rating',
      render: (r) => r.rating ?? '—',
    },
    {
      key: 'comment',
      label: 'Comment',
      render: (r) => <span className="text-truncate d-inline-block" style={{ maxWidth: 320 }} title={r.comment || r.text}>{r.comment || r.text || '—'}</span>,
    },
    {
      key: 'menuItem',
      label: 'Menu item',
      render: (r) => {
        const id = r.menuItem?.menuItemId || r.menuItemId;
        const name = r.menuItem?.name;
        if (!id) return '—';
        return name ? (
          <Link to={`/menu-items/${id}`} className="text-decoration-none">
            {name}
          </Link>
        ) : (
          <Link to={`/menu-items/${id}`} className="font-monospace small text-decoration-none">
            {id}
          </Link>
        );
      },
    },
    {
      key: 'user',
      label: 'User',
      render: (r) => {
        const id = r.user?.userId || r.userId;
        const name = r.user?.name || r.userName;
        if (!id) return '—';
        return name ? (
          <Link to={`/app-users/${id}`} className="text-decoration-none">
            {name}
          </Link>
        ) : (
          <Link to={`/app-users/${id}`} className="font-monospace small text-decoration-none">
            {id}
          </Link>
        );
      },
    },
    {
      key: 'created',
      label: 'When',
      render: (r) => formatDate(r.createdAt || r.created_at),
    },
    {
      key: 'actions',
      label: '',
      width: '72px',
      render: (r) => {
        const id = r.reviewId || r.id || r._id;
        return (
          <button
            type="button"
            className="btn-table-icon btn-table-icon--delete"
            onClick={() => setDeleteId(id)}
            title="Delete"
            aria-label="Delete"
          >
            <IconTrash />
          </button>
        );
      },
    },
  ];

  return (
    <>
      <PageHeader title="Reviews" subtitle="Moderation" />
      <ErrorBanner message={error} />
      <Card className="mb-3">
        <CardBody>
          <div className="row g-2 align-items-end">
            <div className="col-md-3">
              <label className="form-label small mb-1">Rating</label>
              <select className="form-select" value={rating} onChange={(e) => setRating(e.target.value)}>
                <option value="">All</option>
                {[5, 4, 3, 2, 1].map((n) => (
                  <option key={n} value={String(n)}>
                    {n} stars
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label small mb-1 text-transparent user-select-none" aria-hidden="true">
                Filter
              </label>
              <FilterSubmitButton onClick={() => setSkip(0)} />
            </div>
          </div>
        </CardBody>
      </Card>
      <Card>
        <CardBody className="p-0">
          {loading ? (
            <div className="d-flex justify-content-center py-5">
              <div className="spinner-border text-primary" />
            </div>
          ) : (
            <DataTable columns={columns} rows={rows} rowKey={(r, i) => r.reviewId || r.id || r._id || i} />
          )}
          <Pagination totalCount={totalCount} skip={skip} limit={limit} onChange={({ skip: s }) => setSkip(s)} />
        </CardBody>
      </Card>

      {deleteId ? (
        <ConfirmDialog
          title="Delete review"
          message="This will remove the review and recompute item ratings. Continue?"
          confirmLabel="Delete"
          danger
          busy={deleting}
          onCancel={() => setDeleteId(null)}
          onConfirm={confirmDelete}
        />
      ) : null}
    </>
  );
}
